import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { redirectExternal } from "@/lib/redirect";
import { toast } from "sonner";
import BackToCockpit from "@/components/BackToCockpit";
import { Plane, Zap, Sparkles, Users, Building2, ArrowRight, Check, Clock, Star, TrendingUp, Loader2, Heart, ShieldCheck } from "lucide-react";

/**
 * /empty-leg — public marketing page + interest-capture flow for both
 * riders (annual membership) and operators (revenue-share partners).
 * Payment integration (Stripe Connect + verified ACH) is a future release —
 * today we route both sides to a concierge onboarding queue.
 */
export default function EmptyLegProgram() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [showOpForm, setShowOpForm] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/empty-leg/status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [user?.id]);

  const isOperator = user?.role === "charter_operator";
  const riderJoined = !!status?.rider;
  const opJoined = !!status?.operator;
  // "is_crew" = user has a crew hat at all (shows the crew section)
  // "crew_perk_eligible" = user actually qualifies (paid + complete profile)
  const isCrew = !!status?.is_crew;
  const crewEligible = !!status?.crew_perk_eligible;
  const crewGate = status?.crew_gate || null;
  const crewEnrolled = riderJoined && status?.rider?.plan === "crew_free" && crewEligible;
  const [enrollingCrew, setEnrollingCrew] = useState(false);

  const enrollCrew = async () => {
    setEnrollingCrew(true);
    try {
      const r = await api.post("/empty-leg/crew/enroll", {});
      toast.success(r.data.message);
      setStatus((s) => ({ ...(s || {}), rider: r.data.record, crew_perk_eligible: true }));
    } catch (e) {
      // Server sends a structured 403 with the missing checklist.
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.gate) {
        toast.error(detail.message || "Not eligible yet — complete the checklist.");
        setStatus((s) => ({ ...(s || {}), crew_gate: detail.gate, crew_perk_eligible: false }));
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't enroll — try again.");
      }
    } finally { setEnrollingCrew(false); }
  };

  // Rider Stripe checkout — $10,000/yr recurring subscription.
  const RIDER_LOOKUP_KEY = "pjp_empty_leg_rider_yearly";
  const [payingRider, setPayingRider] = useState(false); // eslint-disable-line no-unused-vars
  const payRider = async () => {
    if (!user) {
      // Guest — bounce to signup and resume checkout right after.
      sessionStorage.setItem("pjp_pending_checkout", RIDER_LOOKUP_KEY);
      window.location.href = "/register?next=%2Fempty-leg";
      return;
    }
    // Route through Digital ID upsell interstitial (single Stripe session).
    setPayingRider(true);
    window.location.href = "/membership/addon?tier=empty-leg-rider";
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      {user && <BackToCockpit />}

      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl border border-white/10"
        data-testid="empty-leg-hero"
        style={{
          backgroundImage:
            "linear-gradient(100deg, rgba(5,8,16,0.92) 0%, rgba(5,8,16,0.78) 38%, rgba(5,8,16,0.45) 72%, rgba(5,8,16,0.25) 100%), url('https://customer-assets-lqy194kg.emergentagent.net/job_crew-match-6/artifacts/7rqiks6c_OiBgEcJ7sAbH_R7Dq6ybICeXj3YloFoJs_6YAEKxNYDVOuixzrQz8d_qQ5zHWELqhj88b0pQ0ylwXNMwNmld1n5BZf8WrQkygjk9nQg87aW5CbDdvjGMHsS3BOaq-Rl0sOkH9kGvRZxffWxgAF9MoB1MEHvBRR9MDspSMBf20nwVguTUOR4WR1JpnvtrTfUK.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        {/* HUD scanline overlay for editorial/tech feel */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(75,156,255,0.6) 0px, rgba(75,156,255,0.6) 1px, transparent 1px, transparent 3px)",
          }}
        />
        <div className="relative px-6 md:px-10 py-12 md:py-20 max-w-3xl">
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// PJP · JUMPSEAT PROGRAM</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95] mt-2 drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]">
            Fly the seats the world <span className="text-cyan">forgot</span>.
          </h1>
          <p className="mt-4 text-slate-200 max-w-2xl text-lg drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)]">
            Every day, private jets fly hundreds of thousands of empty miles repositioning between trips.
            The <b>PJP JumpSeat Program</b> puts you in those seats — for a fraction of retail — before the ferry lifts.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {isCrew ? (
              <a
                href="#crew"
                data-testid="el-cta-crew"
                className="btn-hud btn-hud-solid"
                style={{ background: "#2ED573", borderColor: "#2ED573", color: "#0A0F1A" }}
              >
                <ShieldCheck className="h-3.5 w-3.5" /> PJP CREW · FREE ACCESS
              </a>
            ) : (
              <a
                href="#rider"
                data-testid="el-cta-rider"
                className="btn-hud btn-hud-solid"
              >
                <Users className="h-3.5 w-3.5" /> JOIN AS RIDER · $10,000/YR
              </a>
            )}
            <a
              href="#operator"
              data-testid="el-cta-operator"
              className="btn-hud"
            >
              <Building2 className="h-3.5 w-3.5" /> OPERATORS · GET PAID <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-[11px] text-white/70">
            <span className="mono uppercase tracking-widest">// Live status:</span>
            <span className="flex items-center gap-1"><Zap className="h-3 w-3 text-cyan" /> Concierge onboarding · rider seats limited</span>
            <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3 text-avail" /> 15% revenue share to participating operators</span>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="mt-14 grid md:grid-cols-3 gap-4">
        <Step n="01" title="Set your regions" body="Tell us where you'd fly from and where you want to end up. We match your preferences with operator repositioning legs in real time." />
        <Step n="02" title="Get first look" body="When an operator drops an empty leg on your route, you're the first person notified — before it hits public marketplaces." />
        <Step n="03" title="Book at a fraction" body="Ride at up to 75% off retail charter. Same aircraft, same operators, same crew — just seats that would otherwise fly empty." />
      </div>

      {/* Rider section */}
      <section id="rider" className="mt-16 scroll-mt-24">
        <div className="grid md:grid-cols-[1.2fr_1fr] gap-8 items-start">
          <div>
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// FOR RIDERS</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-1">Annual all-access membership</h2>
            <p className="mt-3 text-slate-300">
              One flat $10,000/year unlocks unlimited empty-leg searches, priority notifications, and access to the concierge desk. No per-flight commissions — you pay only the discounted seat cost the operator sets.
            </p>
            <p className="mt-2 text-[12px] text-white/50 italic">
              Working PJP crew (pilots, FAs, mechanics) get this program free with their membership — see the CREW section below.
            </p>
            <ul className="mt-5 space-y-3 text-[13px]">
              <Perk t="First look at every empty leg on your route" />
              <Perk t="Push + SMS the moment a matching leg drops" />
              <Perk t="Priority pricing over public marketplaces" />
              <Perk t="Concierge desk to book, coordinate ground, and re-route" />
              <Perk t="Bring a +1 · pets allowed on participating operators" />
              <Perk t="Empty-leg activity synced to your PJP wallet for tracking" />
            </ul>
          </div>

          {/* Rider price card */}
          <div
            className="rounded-2xl border border-cyan/40 p-6 relative overflow-hidden"
            style={{ background: "linear-gradient(160deg, rgba(75,156,255,0.14) 0%, rgba(5,8,16,0.9) 60%)" }}
          >
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan">// RIDER · ANNUAL</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display text-5xl font-bold text-white leading-none">$10,000</div>
              <div className="text-white/50 text-sm mb-1">/ year</div>
            </div>
            <div className="text-[11px] text-white/50 mt-1">Recurring · cancel anytime · secure Stripe checkout</div>

            <ul className="mt-5 space-y-1.5 text-[12px] text-white/70">
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-cyan flex-shrink-0 mt-0.5" /> Unlimited empty-leg bookings for one year</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-cyan flex-shrink-0 mt-0.5" /> All PJP participating operators</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-cyan flex-shrink-0 mt-0.5" /> Priority alerts + concierge desk</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-cyan flex-shrink-0 mt-0.5" /> Cancel anytime · unused balance credited to PJP Wallet</li>
            </ul>

            {status?.rider?.plan === "paid_rider_yearly" && status?.rider?.status === "active" ? (
              <div
                className="mt-5 rounded-lg border p-3 text-center"
                style={{ borderColor: "rgba(46,213,115,0.45)", background: "rgba(46,213,115,0.10)" }}
                data-testid="el-rider-active"
              >
                <div className="mono text-[10px] tracking-widest uppercase flex items-center justify-center gap-1" style={{ color: "#2ED573" }}>
                  <Check className="h-3 w-3" /> Rider membership active
                </div>
                <div className="text-[11px] text-white/70 mt-1">
                  Manage or cancel from <Link to="/settings/account" className="underline">Account Settings</Link>.
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={payRider}
                disabled={payingRider}
                data-testid="el-rider-pay-now"
                className="mt-5 btn-hud btn-hud-solid w-full justify-center disabled:opacity-40"
              >
                {payingRider ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" /> JOIN &amp; PAY NOW · $10,000/YR <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
            )}
            <div className="mt-2 text-[10.5px] text-white/45 mono text-center">
              Secure Stripe checkout · US$10,000/year · cancel anytime
            </div>
          </div>
        </div>
      </section>

      {/* Crew perk section — only rendered when the logged-in user is a working PJP crew member */}
      {isCrew && (
        <section id="crew" className="mt-16 scroll-mt-24">
          <div
            className="relative overflow-hidden rounded-2xl border p-6 md:p-8"
            data-testid="el-crew-panel"
            style={{
              borderColor: "rgba(46,213,115,0.45)",
              background: "linear-gradient(140deg, rgba(46,213,115,0.14) 0%, rgba(5,8,16,0.92) 55%)",
            }}
          >
            <div className="grid md:grid-cols-[1.4fr_1fr] gap-8 items-start">
              <div>
                <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: "#2ED573" }}>
                  // PJP CREW PERK · YOU FLY FREE
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 leading-[0.95]">
                  You're crew. <span style={{ color: "#2ED573" }}>Empty legs are on the house.</span>
                </h2>
                <p className="mt-4 text-slate-300 max-w-2xl">
                  Because you power this industry, PJP gives every active <b>pilot, flight attendant, and mechanic</b> member the JumpSeat Program <b>free</b> — a $10,000/yr rider benefit, waived for as long as your PJP membership is active. Get seated on repositioning legs going where you already need to be. Commute home, catch a JumpSeat to recurrent, or ride to your next trip without draining a paycheck on airline chaos.
                </p>
                <ul className="mt-6 space-y-3 text-[13px]">
                  <Perk t="Same first-look alerts as $10K/yr paid riders" />
                  <Perk t="Priority routing when your home base matches the empty ferry" />
                  <Perk t="Concierge desk coordinates ground, catering, badging, +1 seats" />
                  <Perk t="Bring a spouse or a fellow crew — extra seats often open" />
                </ul>
                <div className="mt-5 text-[11px] text-white/50 mono">
                  Personal / commuting travel only · not for resale · membership must be current
                </div>
              </div>

              {/* Enroll card */}
              <div
                className="rounded-2xl border p-6 relative overflow-hidden"
                style={{
                  borderColor: "rgba(46,213,115,0.55)",
                  background: "linear-gradient(160deg, rgba(46,213,115,0.18) 0%, rgba(5,8,16,0.9) 60%)",
                }}
              >
                <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: "#2ED573" }}>
                  // CREW · INCLUDED
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <div className="font-display text-5xl font-bold text-white leading-none">$0</div>
                  <div className="text-white/50 text-sm mb-1">/ year for you</div>
                </div>
                <div className="text-[11px] text-white/50 mt-1 line-through">Normally $10,000 / year</div>

                <ul className="mt-5 space-y-1.5 text-[12px] text-white/70">
                  <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> Free with your PJP crew membership</li>
                  <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> All PJP participating operators</li>
                  <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> Priority alerts + concierge desk</li>
                  <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> Bring +1 · pets on participating operators</li>
                </ul>

                {crewEnrolled ? (
                  <div
                    className="mt-5 rounded-lg border p-3 text-center"
                    style={{ borderColor: "rgba(46,213,115,0.45)", background: "rgba(46,213,115,0.10)" }}
                    data-testid="el-crew-enrolled"
                  >
                    <div className="mono text-[10px] tracking-widest uppercase flex items-center justify-center gap-1" style={{ color: "#2ED573" }}>
                      <ShieldCheck className="h-3 w-3" /> Enrolled · free crew access live
                    </div>
                    <div className="text-[11px] text-white/70 mt-1">
                      You'll get first-look alerts the moment a leg matches your home base.
                    </div>
                  </div>
                ) : crewEligible ? (
                  <button
                    type="button"
                    onClick={enrollCrew}
                    disabled={enrollingCrew}
                    data-testid="el-crew-enroll"
                    className="mt-5 btn-hud btn-hud-solid w-full justify-center disabled:opacity-40"
                    style={{ background: "#2ED573", borderColor: "#2ED573", color: "#0A0F1A" }}
                  >
                    {enrollingCrew ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShieldCheck className="h-3.5 w-3.5" /> ACTIVATE FREE ACCESS</>}
                  </button>
                ) : (
                  <div
                    className="mt-5 rounded-lg border p-4"
                    data-testid="el-crew-gate-checklist"
                    style={{ borderColor: "rgba(244,196,67,0.35)", background: "rgba(244,196,67,0.08)" }}
                  >
                    <div className="mono text-[10px] tracking-widest uppercase flex items-center gap-1" style={{ color: "#F4C443" }}>
                      <Clock className="h-3 w-3" /> Not activated yet · complete these to unlock
                    </div>
                    <ul className="mt-3 space-y-2 text-[12px]">
                      {(crewGate?.checks || []).map((c) => (
                        <li key={c.key} data-testid={`el-crew-check-${c.key}`} className="flex items-start gap-2">
                          {c.ok ? (
                            <Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} />
                          ) : (
                            <span
                              className="h-3.5 w-3.5 rounded-full border flex-shrink-0 mt-0.5"
                              style={{ borderColor: "rgba(244,196,67,0.7)" }}
                              aria-hidden
                            />
                          )}
                          <div className="min-w-0">
                            <div className={c.ok ? "text-white/80" : "text-white/90 font-medium"}>{c.label}</div>
                            {!c.ok && (
                              <div className="text-[11px] text-white/55 mt-0.5">{c.hint}</div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(crewGate?.missing || []).some((m) => m.key === "paid_membership") && (
                        <Link to="/membership" data-testid="el-crew-gate-cta-pay" className="btn-hud btn-hud-solid">
                          <ArrowRight className="h-3.5 w-3.5" /> Complete membership payment
                        </Link>
                      )}
                      {(crewGate?.missing || []).some((m) => ["identity","home_base","profile_photo","type_rating"].includes(m.key)) && (
                        <Link to="/profile" data-testid="el-crew-gate-cta-profile" className="btn-hud">
                          <ArrowRight className="h-3.5 w-3.5" /> Complete profile
                        </Link>
                      )}
                      {(crewGate?.missing || []).some((m) => m.key === "verified_document") && (
                        <Link to="/documents" data-testid="el-crew-gate-cta-docs" className="btn-hud">
                          <ArrowRight className="h-3.5 w-3.5" /> Upload credentials
                        </Link>
                      )}
                      {(crewGate?.missing || []).some((m) => m.key === "faa_verified") && (
                        <Link to="/profile#faa" data-testid="el-crew-gate-cta-faa" className="btn-hud">
                          <ArrowRight className="h-3.5 w-3.5" /> Run FAA verification
                        </Link>
                      )}
                    </div>
                    <div className="mt-3 text-[10.5px] text-white/50 mono">
                      Free access activates automatically the moment every box is checked.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Operator section */}
      <section id="operator" className="mt-20 scroll-mt-24">
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 items-start">
          {/* Operator pricing card */}
          <div
            className="rounded-2xl border border-amber-400/40 p-6 relative overflow-hidden md:order-1"
            style={{ background: "linear-gradient(160deg, rgba(251,191,36,0.10) 0%, rgba(5,8,16,0.9) 60%)" }}
          >
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-amber-300">// OPERATOR · REVENUE SHARE</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display text-5xl font-bold text-white leading-none">15%</div>
              <div className="text-white/50 text-sm mb-1">of every rider fee</div>
            </div>
            <div className="text-[11px] text-white/50 mt-1">Free to join · paid quarterly · no exclusivity</div>

            <ul className="mt-5 space-y-1.5 text-[12px] text-white/70">
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-amber-300 flex-shrink-0 mt-0.5" /> Monetize legs that would otherwise fly empty</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-amber-300 flex-shrink-0 mt-0.5" /> No exclusivity — keep listing on your marketplaces</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-amber-300 flex-shrink-0 mt-0.5" /> Auto-generated feed from your fleet activity</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 text-amber-300 flex-shrink-0 mt-0.5" /> Vetted PJP riders · KYC + payment guaranteed</li>
            </ul>

            {!user ? (
              <Link to="/register" data-testid="el-op-signup-link" className="mt-5 btn-hud w-full justify-center">
                <Building2 className="h-3.5 w-3.5" /> SIGN UP AS OPERATOR
              </Link>
            ) : !isOperator ? (
              <div className="mt-5 rounded-lg border border-white/15 bg-white/5 p-3 text-center">
                <div className="mono text-[10px] tracking-widest uppercase text-white/50">Operator accounts only</div>
                <div className="text-[11px] text-white/60 mt-1">Log in with a certificated operator account to enroll.</div>
              </div>
            ) : opJoined ? (
              <div className="mt-5 rounded-lg border border-avail/40 bg-avail/10 p-3 text-center">
                <div className="mono text-[10px] tracking-widest uppercase text-avail flex items-center justify-center gap-1">
                  <Check className="h-3 w-3" /> Enrollment recorded
                </div>
                <div className="text-[11px] text-white/70 mt-1">Our BD team will finalize your rev-share within 3 business days.</div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowOpForm(true)}
                data-testid="el-op-join"
                className="mt-5 btn-hud w-full justify-center border-amber-400/50 text-amber-300 hover:bg-amber-400/10"
              >
                <Sparkles className="h-3.5 w-3.5" /> ENROLL MY FLEET
              </button>
            )}
          </div>

          <div className="md:order-2">
            <div className="mono text-[10px] tracking-[0.24em] uppercase text-amber-300">// FOR OPERATORS</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-1">Turn empty ferries into revenue <span className="text-white/50">·</span> and goodwill.</h2>
            <p className="mt-3 text-slate-300">
              Every operator has legs that fly empty for repositioning. The PJP JumpSeat Program plugs those into a curated rider audience willing to pay real money for those seats.
              You keep 100% of the seat fees you set, plus you earn a share of every rider's $10K annual membership.
            </p>
            <ul className="mt-5 space-y-3 text-[13px]">
              <Perk t="Simple onboarding — just share your typical empty-leg patterns" />
              <Perk t="Auto-list from your existing PJP fleet or send us a scheduling feed" />
              <Perk t="Members are vetted, insured, and pre-paid — no chargeback risk" />
              <Perk t="Quarterly payouts to your PJP Wallet · export to bank when Stripe Connect goes live" />
            </ul>

            {/* Crew-lift call-out — operators comp seats to PJP crew when the ferry would fly empty anyway */}
            <div
              className="mt-6 rounded-xl border p-4"
              data-testid="el-operator-crew-callout"
              style={{
                borderColor: "rgba(46,213,115,0.35)",
                background: "linear-gradient(140deg, rgba(46,213,115,0.10), rgba(5,8,16,0.6))",
              }}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg border" style={{ borderColor: "rgba(46,213,115,0.45)", background: "rgba(46,213,115,0.10)" }}>
                  <Heart className="h-4 w-4" style={{ color: "#2ED573" }} />
                </div>
                <div className="flex-1">
                  <div className="mono text-[10px] tracking-widest uppercase" style={{ color: "#2ED573" }}>
                    // BONUS · FLY A CREW HOME
                  </div>
                  <div className="font-display text-lg font-semibold text-white mt-1">
                    Seat a PJP pilot, FA or mechanic on your empty ferry — free.
                  </div>
                  <p className="text-[12.5px] text-white/70 mt-2 leading-relaxed">
                    Every crew member on PJP gets JumpSeat access included with their membership.
                    When your leg is already flying empty, opt-in to fill open seats with PJP-verified crew commuting home,
                    catching recurrent, or repositioning for their next trip. <b style={{ color: "#2ED573" }}>Zero cost to you</b>,
                    massive goodwill for the industry, and every seated crew member is a walking billboard for your operation.
                  </p>
                  <ul className="mt-3 space-y-1.5 text-[12px] text-white/70">
                    <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> PJP-verified working crew only · FAA cross-checked · insured</li>
                    <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> You choose seat count, dates, and blackout legs</li>
                    <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> Bumpable — paying charter always takes priority</li>
                    <li className="flex gap-2"><Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#2ED573" }} /> Recognized as a "PJP Crew-Lift Partner" across the network</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showOpForm && isOperator && !opJoined && (
          <OperatorForm
            onCancel={() => setShowOpForm(false)}
            onSubmitted={(record) => {
              setStatus((s) => ({ ...(s || {}), operator: record }));
              setShowOpForm(false);
            }}
          />
        )}
      </section>

      {/* Trust bar */}
      <div className="mt-16 grid md:grid-cols-3 gap-3">
        <Trust icon={Clock} title="First-look advantage" body="Riders see legs before public marketplaces get them." />
        <Trust icon={Star} title="Concierge onboarding" body="Every new rider is manually greeted by our team." />
        <Trust icon={Building2} title="Real operators" body="Only Part 135 / EASA-approved carriers on the network." />
      </div>

      {/* Final CTA */}
      <div className="mt-14 rounded-xl border border-cyan/25 p-6 flex flex-wrap items-center justify-between gap-4"
           style={{ background: "linear-gradient(150deg, rgba(75,156,255,0.10), rgba(5,8,16,0.9))" }}>
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan">// READY TO FLY?</div>
          <div className="font-display text-2xl font-bold mt-1">The seats fly with or without you.</div>
        </div>
        <div className="flex gap-3">
          {isCrew ? (
            <a
              href="#crew"
              className="btn-hud btn-hud-solid"
              data-testid="el-final-crew"
              style={{ background: "#2ED573", borderColor: "#2ED573", color: "#0A0F1A" }}
            >
              <ShieldCheck className="h-3.5 w-3.5" /> ACTIVATE CREW ACCESS
            </a>
          ) : (
            <a href="#rider" className="btn-hud btn-hud-solid" data-testid="el-final-rider"><Users className="h-3.5 w-3.5" /> JOIN AS RIDER</a>
          )}
          <a href="#operator" className="btn-hud" data-testid="el-final-op"><Building2 className="h-3.5 w-3.5" /> OPERATORS</a>
        </div>
      </div>
    </div>
  );
}

/* ============================ Small pieces ============================ */

function Step({ n, title, body }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
      <div className="mono text-[10px] tracking-widest uppercase text-cyan/70">{n}</div>
      <div className="font-display text-lg font-semibold mt-1">{title}</div>
      <div className="text-[12px] text-white/60 mt-2">{body}</div>
    </div>
  );
}

function Perk({ t }) {
  return (
    <li className="flex gap-2 text-white/80">
      <Check className="h-4 w-4 text-cyan flex-shrink-0 mt-0.5" />
      <span>{t}</span>
    </li>
  );
}

function Trust({ icon: Icon, title, body }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3">
      <div className="p-1.5 rounded-md border border-white/10">
        <Icon className="h-4 w-4 text-cyan" />
      </div>
      <div>
        <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">{title}</div>
        <div className="text-[12px] text-white/60 mt-0.5">{body}</div>
      </div>
    </div>
  );
}

function RiderForm({ onCancel, onSubmitted }) {
  // Retained but unused after Stripe rider checkout replaced the concierge intake.
  // Kept here for future use if we want soft-lead capture separately from paid signup.
  return null;
}

function OperatorForm({ onCancel, onSubmitted }) {
  const [fleet, setFleet] = useState("");
  const [monthly, setMonthly] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/empty-leg/operator/join-interest", {
        fleet_size: fleet ? Number(fleet) : null,
        typical_empty_legs_per_month: monthly ? Number(monthly) : null,
        notes: notes || null,
      });
      toast.success(r.data.message);
      onSubmitted(r.data.record);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-xl border border-amber-400/40 bg-amber-400/[0.06] p-5" data-testid="el-op-form">
      <div className="mono text-[10px] tracking-widest uppercase text-amber-300 mb-3">// Operator · enrollment</div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Fleet size</div>
          <input data-testid="el-op-fleet" type="number" min="1" value={fleet} onChange={(e) => setFleet(e.target.value)} placeholder="e.g. 12" className="input w-full" />
        </div>
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Empty legs / month</div>
          <input data-testid="el-op-monthly" type="number" min="0" value={monthly} onChange={(e) => setMonthly(e.target.value)} placeholder="e.g. 25" className="input w-full" />
        </div>
        <div>
          <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Notes</div>
          <input data-testid="el-op-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Regions, base airports…" className="input w-full" />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/15 text-white/60 hover:bg-white/5">Cancel</button>
        <button type="button" data-testid="el-op-submit" onClick={submit} disabled={busy}
          className="mono text-[10px] tracking-widest uppercase px-4 py-2 rounded bg-amber-400 text-obsidian font-bold disabled:opacity-40 flex items-center gap-1.5">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} ENROLL FLEET
        </button>
      </div>
    </div>
  );
}
